import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ThemedBackground } from '../components/ThemedBackground';
import { ArrowLeft, Trophy, Users, TrendingUp, Target, Zap, Swords } from 'lucide-react';
import { LeaderboardPlayer, GameHistoryItem, PlayerRoundRecord } from '../modules/stats/types';
import { statsDataService } from '../modules/stats/dataService';
import { dojoSeasonService } from '../modules/stats/dojoSeasonService';
import { DojosRow, SeasonsRow } from '../types/database';
import RoleStatsChart from '../components/RoleStatsChart';
import WinStreakTracker from '../components/WinStreakTracker';
import OpponentAnalysis from '../components/OpponentAnalysis';
import TimeFilter from '../components/TimeFilter';

export default function PlayerDetail() {
  const { playerNumber } = useParams<{ playerNumber: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // V145: 支持通过URL参数传入nickname作为唯一标识
  const urlNickname = searchParams.get('nickname');
  const [player, setPlayer] = useState<LeaderboardPlayer | null>(null);
  const [allGames, setAllGames] = useState<GameHistoryItem[]>([]);
  const [filteredGames, setFilteredGames] = useState<GameHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'roles' | 'streak' | 'opponents'>('overview');
  const [_allPlayers, _setAllPlayers] = useState<{ player_number: number; player_nickname: string }[]>([]);
  // V145: 赛季和道场筛选状态
  const [seasons, setSeasons] = useState<SeasonsRow[]>([]);
  const [dojos, setDojos] = useState<DojosRow[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<string>('all');
  const [selectedDojo, setSelectedDojo] = useState<string>('all');
  // V145: 当前玩家昵称（用于数据绑定）
  const [currentPlayerNickname, setCurrentPlayerNickname] = useState<string>('');

  useEffect(() => {
    // V339: 尝试从缓存读取预加载的数据
    const cachedData = sessionStorage.getItem('player-detail-cache');
    if (cachedData && urlNickname) {
      try {
        const parsed = JSON.parse(cachedData);
        // 检查缓存是否匹配当前昵称且未过期（5 分钟内有效）
        if (parsed.nickname === urlNickname && Date.now() - parsed.timestamp < 5 * 60 * 1000) {
          console.log('[V339] 使用缓存的玩家详情数据:', parsed.nickname);
          // 使用缓存数据快速初始化
          if (parsed.data) {
            setPlayer({
              player_number: 0,
              player_name: parsed.nickname,
              total_score: parsed.data.totalScore || 0,
              total_games: parsed.data.totalGames || 0,
              wins: parsed.data.wins || 0,
              losses: parsed.data.losses || 0,
              good_wins: parsed.data.goodWins || 0,
              evil_wins: parsed.data.evilWins || 0,
              mvp_count: parsed.data.mvpCount || 0,
              win_rate: parsed.data.winRate || 0,
              avg_score: parsed.data.averageScore || 0,
              percival_wins: 0,
              percival_games: 0,
              merlin_wins: 0,
              merlin_games: 0,
              lancelot_good_wins: 0,
              lancelot_good_games: 0,
              lancelot_evil_wins: 0,
              lancelot_evil_games: 0,
              loyal_wins: 0,
              loyal_games: 0,
              mordred_wins: 0,
              mordred_games: 0,
              morgana_wins: 0,
              morgana_games: 0,
              assassin_wins: 0,
              assassin_games: 0,
              assassin_merlin_kills: 0,
              minion_wins: 0,
              minion_games: 0,
              oberon_wins: 0,
              oberon_games: 0,
            } as LeaderboardPlayer);
            
            // V339: 存储角色统计和对阵分析数据供后续使用
            const roleStats = parsed.data.roleStats || [];
            const vsPlayers = parsed.data.vsPlayers || [];
            sessionStorage.setItem('player-detail-role-stats', JSON.stringify(roleStats));
            sessionStorage.setItem('player-detail-vs-players', JSON.stringify(vsPlayers));
            console.log('[V339] 已缓存角色统计和对阵数据:', {
              roleStatsCount: roleStats.length,
              vsPlayersCount: vsPlayers.length,
            });
          }
          setIsLoading(false);
        } else {
          sessionStorage.removeItem('player-detail-cache');
        }
      } catch (err) {
        console.error('[V339] 解析缓存数据失败:', err);
        sessionStorage.removeItem('player-detail-cache');
      }
    }
    
    loadPlayerData();
    loadDojosAndSeasons();
  }, [playerNumber, urlNickname]);

  // V1.2: 加载道场和赛季数据
  const loadDojosAndSeasons = async () => {
    try {
      const [dojosData, seasonsData] = await Promise.all([
        dojoSeasonService.loadDojos(),
        dojoSeasonService.loadSeasons(),
      ]);
      setDojos(dojosData);
      setSeasons(seasonsData);
    } catch (err) {
      console.error('[V1.2] 加载道场/赛季失败:', err);
    }
  };

  // V147: 修复数据一致性 - 从过滤的游戏中重新计算玩家统计
  const calculatePlayerStatsFromGames = (games: GameHistoryItem[], targetNickname: string): LeaderboardPlayer | null => {
    // V147: 使用 Map 去重，键为 nickname + game_record_id
    const recordMap = new Map<string, PlayerRoundRecord>();
    
    games.forEach(game => {
      // V147: 严格按 nickname 匹配
      const player = game.players?.find(p => p.player_nickname === targetNickname);
      if (player) {
        // V147: 使用 nickname + game_record_id 作为唯一键
        const uniqueKey = `${player.player_nickname}_${player.game_record_id || game.id}`;
        if (!recordMap.has(uniqueKey)) {
          recordMap.set(uniqueKey, player);
        }
      }
    });
    
    const playerRecords = Array.from(recordMap.values());
    
    console.log('[V147] calculatePlayerStatsFromGames:', {
      targetNickname,
      foundRecords: playerRecords.length,
      gamesCount: games.length,
    });

    if (playerRecords.length === 0) return null;

    const playerName = targetNickname;
    let wins = 0;
    let goodWins = 0;
    let evilWins = 0;
    let mvpCount = 0;
    
    // V147: 所有10种角色的统计
    const roleStats: Record<string, { games: number; wins: number }> = {
      percival: { games: 0, wins: 0 },
      merlin: { games: 0, wins: 0 },
      lancelot_good: { games: 0, wins: 0 },
      lancelot_evil: { games: 0, wins: 0 },
      loyal: { games: 0, wins: 0 },
      mordred: { games: 0, wins: 0 },
      morgana: { games: 0, wins: 0 },
      assassin: { games: 0, wins: 0 },
      minion: { games: 0, wins: 0 },
      oberon: { games: 0, wins: 0 },
    };

    playerRecords.forEach(record => {
      // V147: 使用 score_change 判断胜负（正分=胜利）
      const isWin = (record.score_change || 0) > 0;
      if (isWin) {
        wins++;
        if (record.faction === 'good') goodWins++;
        if (record.faction === 'evil') evilWins++;
      }
      if (record.is_mvp) mvpCount++;

      // V147: 统计角色数据
      const role = record.player_role;
      if (role && roleStats[role]) {
        roleStats[role].games++;
        if (isWin) {
          roleStats[role].wins++;
        }
      }
    });

    // V147: 计算总场次
    const losses = playerRecords.length - wins;
    const totalGames = wins + losses;
    const totalScore = playerRecords.reduce((sum, r) => sum + (r.score_change || 0), 0);
    
    // V147: 数据一致性校验 - 确保总场次 = 角色明细之和
    const roleGamesSum = 
      roleStats.percival.games +
      roleStats.merlin.games +
      roleStats.lancelot_good.games +
      roleStats.lancelot_evil.games +
      roleStats.loyal.games +
      roleStats.mordred.games +
      roleStats.morgana.games +
      roleStats.assassin.games +
      roleStats.minion.games +
      roleStats.oberon.games;
    
    if (roleGamesSum !== totalGames) {
      console.error('[V147] ⚠️ 数据不一致:', {
        targetNickname,
        totalGames,
        roleGamesSum,
        diff: totalGames - roleGamesSum,
      });
    }
    
    console.log('[V147] 总场次计算:', {
      targetNickname,
      totalGames,
      roleGamesSum,
      isConsistent: roleGamesSum === totalGames,
    });
    
    return {
      player_number: playerRecords[0]?.player_number || 0,
      player_name: playerName,
      total_score: totalScore,
      total_games: totalGames,
      wins: wins,
      losses: losses,
      good_wins: goodWins,
      good_losses: roleStats.percival.games + roleStats.merlin.games + roleStats.lancelot_good.games + roleStats.loyal.games + roleStats.oberon.games - goodWins,
      evil_wins: evilWins,
      evil_losses: roleStats.lancelot_evil.games + roleStats.mordred.games + roleStats.morgana.games + roleStats.assassin.games + roleStats.minion.games - evilWins,
      mvp_count: mvpCount,
      win_rate: totalGames > 0 ? Math.round((wins / totalGames) * 100) : 0,
      avg_score: totalGames > 0 ? totalScore / totalGames : 0,
      percival_wins: roleStats.percival.wins,
      percival_games: roleStats.percival.games,
      merlin_wins: roleStats.merlin.wins,
      merlin_games: roleStats.merlin.games,
      lancelot_good_wins: roleStats.lancelot_good.wins,
      lancelot_good_games: roleStats.lancelot_good.games,
      lancelot_evil_wins: roleStats.lancelot_evil.wins,
      lancelot_evil_games: roleStats.lancelot_evil.games,
      loyal_wins: roleStats.loyal.wins,
      loyal_games: roleStats.loyal.games,
      mordred_wins: roleStats.mordred.wins,
      mordred_games: roleStats.mordred.games,
      morgana_wins: roleStats.morgana.wins,
      morgana_games: roleStats.morgana.games,
      assassin_wins: roleStats.assassin.wins,
      assassin_games: roleStats.assassin.games,
      assassin_merlin_kills: 0,
      minion_wins: roleStats.minion.wins,
      minion_games: roleStats.minion.games,
      oberon_wins: roleStats.oberon.wins,
      oberon_games: roleStats.oberon.games,
    };
  };

  const loadPlayerData = async () => {
    // V145: 优先使用URL传入的nickname，否则使用playerNumber查找对应的nickname
    let targetNickname = urlNickname || '';
    
    setIsLoading(true);
    try {
      // V145: 先加载历史记录
      const gamesResult = await statsDataService.loadHistoryGames('all', 'all');
      const games = gamesResult.data;
      setAllGames(games);
      setFilteredGames(games);
      
      // V145: 如果没有传入nickname，通过playerNumber查找
      if (!targetNickname && playerNumber) {
        const targetPlayerRecords = games
          .flatMap(g => g.players || [])
          .filter(p => p.player_number === parseInt(playerNumber));
        targetNickname = targetPlayerRecords[0]?.player_nickname || '';
      }
      
      // V145: 设置当前玩家昵称
      setCurrentPlayerNickname(targetNickname);
      
      console.log('[V145] 目标玩家信息:', { 
        playerNumber, 
        targetNickname,
        urlNickname,
      });
      
      // V145: 加载所有统计并按 nickname 精确匹配
      const allStats = await statsDataService.loadPlayerStats('all', 'all');
      
      // V145: 使用 nickname 精确匹配
      let playerData = allStats.playerStats.find(p => p.player_name === targetNickname);
      
      // V145: 增强调试日志
      console.log('[V145] 玩家统计数据:', {
        found: !!playerData,
        targetNickname,
        playerName: playerData?.player_name,
        totalGames: playerData?.total_games,
        wins: playerData?.wins,
        losses: playerData?.losses,
        winRate: playerData?.win_rate,
      });
      
      // 提取所有玩家
      const playerSet = new Map<number, string>();
      games.forEach((g: GameHistoryItem) => {
        g.players?.forEach((p) => {
          if (!playerSet.has(p.player_number)) {
            playerSet.set(p.player_number, p.player_nickname || `玩家${p.player_number}`);
          }
        });
      });
      _setAllPlayers(Array.from(playerSet.entries()).map(([num, name]) => ({
        player_number: num,
        player_nickname: name,
      })));
      
      if (playerData) {
        setPlayer(playerData);
      } else {
        // V145: 如果没有找到数据，创建一个空玩家对象用于显示
        console.warn('[V145] 未找到玩家数据:', { targetNickname });
        setPlayer({
          player_number: parseInt(playerNumber || '0'),
          player_name: targetNickname,
          total_score: 0,
          total_games: 0,
          wins: 0,
          losses: 0,
          good_wins: 0,
          evil_wins: 0,
          mvp_count: 0,
          win_rate: 0,
          avg_score: 0,
          percival_wins: 0,
          percival_games: 0,
          merlin_wins: 0,
          merlin_games: 0,
          lancelot_good_wins: 0,
          lancelot_good_games: 0,
          lancelot_evil_wins: 0,
          lancelot_evil_games: 0,
          loyal_wins: 0,
          loyal_games: 0,
          mordred_wins: 0,
          mordred_games: 0,
          morgana_wins: 0,
          morgana_games: 0,
          assassin_wins: 0,
          assassin_games: 0,
          assassin_merlin_kills: 0,
          minion_wins: 0,
          minion_games: 0,
          oberon_wins: 0,
          oberon_games: 0,
        } as LeaderboardPlayer);
      }

      // V339: 尝试从缓存读取角色统计和对阵分析数据
      const cachedRoleStatsStr = sessionStorage.getItem('player-detail-role-stats');
      const cachedVsPlayersStr = sessionStorage.getItem('player-detail-vs-players');
      if (cachedRoleStatsStr && cachedVsPlayersStr && urlNickname === currentPlayerNickname) {
        try {
          const roleStats = JSON.parse(cachedRoleStatsStr);
          const vsPlayers = JSON.parse(cachedVsPlayersStr);
          console.log('[V339] 从缓存加载角色统计和对阵数据:', {
            roleStatsCount: roleStats.length,
            vsPlayersCount: vsPlayers.length,
          });
        } catch (err) {
          console.error('[V339] 解析缓存数据失败:', err);
        }
      }
    } catch (err) {
      console.error('[V145] 加载玩家数据失败:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // V145: 统一的筛选函数，接收所有筛选参数
  const applyFilters = (startDate: Date, endDate: Date, seasonId: string, dojoId: string) => {
    // V145: 增强筛选数据流追踪
    console.log('[V145] 筛选触发:', {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      seasonId,
      dojoId,
      allGamesCount: allGames.length,
    });
    
    // V145: 同步过滤游戏并重新计算玩家统计
    let filtered = allGames.filter(g => {
      if (!g.game_date) return false;
      const gameDate = new Date(g.game_date);
      const isInRange = gameDate >= startDate && gameDate <= endDate;
      return isInRange;
    });
    
    // V145: 应用赛季过滤
    if (seasonId !== 'all') {
      filtered = filtered.filter(g => g.season_id === parseInt(seasonId));
    }
    
    // V145: 应用道场过滤
    if (dojoId !== 'all') {
      filtered = filtered.filter(g => g.dojo_id === parseInt(dojoId));
    }
    
    console.log('[V145] 筛选结果:', {
      originalGamesCount: allGames.length,
      filteredGamesCount: filtered.length,
      filteredRatio: `${filtered.length}/${allGames.length}`,
    });
    
    setFilteredGames(filtered);
    
    // V145: 基于过滤后的游戏重新计算玩家统计数据（使用nickname匹配）
    if (currentPlayerNickname) {
      const filteredPlayerStats = calculatePlayerStatsFromGames(filtered, currentPlayerNickname);
      console.log('[V145] 重新计算玩家统计:', {
        currentPlayerNickname,
        foundStats: !!filteredPlayerStats,
        totalGames: filteredPlayerStats?.total_games,
        wins: filteredPlayerStats?.wins,
        losses: filteredPlayerStats?.losses,
      });
      if (filteredPlayerStats) {
        setPlayer(filteredPlayerStats);
      } else {
        // V145: 筛选后无数据，显示空状态
        setPlayer({
          player_number: parseInt(playerNumber || '0'),
          player_name: currentPlayerNickname,
          total_score: 0,
          total_games: 0,
          wins: 0,
          losses: 0,
          good_wins: 0,
          evil_wins: 0,
          mvp_count: 0,
          win_rate: 0,
          avg_score: 0,
          percival_wins: 0,
          percival_games: 0,
          merlin_wins: 0,
          merlin_games: 0,
          lancelot_good_wins: 0,
          lancelot_good_games: 0,
          lancelot_evil_wins: 0,
          lancelot_evil_games: 0,
          loyal_wins: 0,
          loyal_games: 0,
          mordred_wins: 0,
          mordred_games: 0,
          morgana_wins: 0,
          morgana_games: 0,
          assassin_wins: 0,
          assassin_games: 0,
          assassin_merlin_kills: 0,
          minion_wins: 0,
          minion_games: 0,
          oberon_wins: 0,
          oberon_games: 0,
        } as LeaderboardPlayer);
      }
    }
  };
  
  // V149: 修复筛选逻辑 - 保存时间范围状态
  const [currentTimeRange, setCurrentTimeRange] = useState<{ start: Date; end: Date }>({
    start: new Date('1970-01-01'),
    end: new Date('2099-12-31'),
  });

  // V149: 处理时间筛选
  const handleTimeFilter = (startDate: Date, endDate: Date) => {
    setCurrentTimeRange({ start: startDate, end: endDate });
    // 使用当前状态中的赛季和道场值
    applyFilters(startDate, endDate, selectedSeason, selectedDojo);
  };
  
  // V149: 处理赛季变化 - 使用已保存的时间范围
  const handleSeasonChange = (seasonId: string) => {
    console.log('[V149] 赛季选择变化:', { seasonId });
    setSelectedSeason(seasonId);
    // 使用当前时间范围和新的赛季值、当前的道场值进行筛选
    applyFilters(currentTimeRange.start, currentTimeRange.end, seasonId, selectedDojo);
  };
  
  // V149: 处理道场变化 - 使用已保存的时间范围
  const handleDojoChange = (dojoId: string) => {
    console.log('[V149] 道场选择变化:', { dojoId });
    setSelectedDojo(dojoId);
    // 使用当前时间范围和当前的赛季值、新的道场值进行筛选
    applyFilters(currentTimeRange.start, currentTimeRange.end, selectedSeason, dojoId);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen p-4 flex items-center justify-center">
        <div className="text-avalon-text">加载中...</div>
      </div>
    );
  }

  if (!player) {
    return (
      <div className="min-h-screen p-4 pt-[72px]">
        {/* V189: 固定头部 */}
        <div className="fixed top-0 left-0 right-0 z-50 flex items-center gap-3 px-4 py-3" style={{ backgroundColor: '#1A0F2A', borderBottom: '1px solid #5A4BFF35' }}>
          <button
            onClick={() => navigate('/stats')}
            className="w-10 h-10 rounded-lg bg-avalon-card flex items-center justify-center text-avalon-gold hover:bg-avalon-purple transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-white">玩家详情</h1>
        </div>
        <div className="avalon-card p-8 text-center">
          <Users className="w-12 h-12 mx-auto mb-3 text-avalon-text/50" />
          <p className="text-avalon-text">未找到该玩家</p>
        </div>
      </div>
    );
  }

  return (
    <ThemedBackground pageName="player-detail">
    <div className="min-h-screen p-4 pt-[72px]">
      {/* V189: 固定头部 */}
      <div className="fixed top-0 left-0 right-0 z-50 flex items-center gap-3 px-4 py-3" style={{ backgroundColor: '#1A0F2A', borderBottom: '1px solid #5A4BFF35' }}>
        <button
          onClick={() => navigate('/stats')}
          className="w-10 h-10 rounded-lg bg-avalon-card flex items-center justify-center text-avalon-gold hover:bg-avalon-purple transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-white">{player.player_name}</h1>
        </div>
      </div>

      {/* Tab 切换 */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
        <button
          onClick={() => setActiveTab('overview')}
          className={`flex-1 py-2 px-3 rounded-lg flex items-center justify-center gap-2 transition-colors text-sm whitespace-nowrap ${
            activeTab === 'overview'
              ? 'bg-avalon-purple text-white'
              : 'bg-avalon-card text-avalon-text hover:text-white'
          }`}
        >
          <TrendingUp className="w-4 h-4" />
          <span>总览</span>
        </button>
        <button
          onClick={() => setActiveTab('roles')}
          className={`flex-1 py-2 px-3 rounded-lg flex items-center justify-center gap-2 transition-colors text-sm whitespace-nowrap ${
            activeTab === 'roles'
              ? 'bg-avalon-purple text-white'
              : 'bg-avalon-card text-avalon-text hover:text-white'
          }`}
        >
          <Target className="w-4 h-4" />
          <span>角色统计</span>
        </button>

        <button
          onClick={() => setActiveTab('opponents')}
          className={`flex-1 py-2 px-3 rounded-lg flex items-center justify-center gap-2 transition-colors text-sm whitespace-nowrap ${
            activeTab === 'opponents'
              ? 'bg-avalon-purple text-white'
              : 'bg-avalon-card text-avalon-text hover:text-white'
          }`}
        >
          <Swords className="w-4 h-4" />
          <span>对阵分析</span>
        </button>
      </div>

      {/* V157: 时间筛选器（在总览和角色统计Tab显示）- 改为水平并排布局 */}
      {(activeTab === 'overview' || activeTab === 'roles') && (
        <div className="mb-4">
          <TimeFilter 
            onFilterChange={handleTimeFilter}
            seasons={seasons.map(s => ({ id: String(s.id), name: s.name }))}
            venues={dojos.map(d => ({ id: String(d.id), name: d.name }))}
            onSeasonChange={handleSeasonChange}
            onVenueChange={handleDojoChange}
          />
        </div>
      )}

      {/* 总览面板 */}
      {activeTab === 'overview' && (
        <div className="space-y-4">
          {/* V157: 时间段统计卡片 - 2x2布局，压缩间距 */}
          <div className="grid grid-cols-2 gap-2">
            <div className="avalon-card p-2">
              <div className="flex items-center gap-1 text-avalon-text text-xs mb-0.5">
                <Trophy className="w-3 h-3" />
                <span>总场次</span>
              </div>
              <div className="text-lg font-bold text-white">
                {(player?.total_games || 0) > 0 ? player.total_games : '-'}
              </div>
            </div>
            <div className="avalon-card p-2">
              <div className="flex items-center gap-1 text-avalon-text text-xs mb-0.5">
                <Zap className="w-3 h-3" />
                <span>胜率</span>
              </div>
              <div className={`text-lg font-bold ${
                (player.total_games || 0) === 0 ? 'text-avalon-text' :
                player.win_rate >= 60 ? 'text-green-400' :
                player.win_rate >= 40 ? 'text-avalon-gold' :
                'text-red-400'
              }`}>
                {(player.total_games || 0) > 0 ? `${player.win_rate}%` : '-'}
              </div>
            </div>
            <div className="avalon-card p-2">
              <div className="flex items-center gap-1 text-avalon-text text-xs mb-0.5">
                <Trophy className="w-3 h-3" />
                <span>胜/负</span>
              </div>
              <div className="text-base font-bold text-white">
                {(player.total_games || 0) > 0 ? (
                  <>
                    <span className="text-green-400">{player.wins}</span>
                    <span className="text-avalon-text mx-0.5">/</span>
                    <span className="text-red-400">{player.losses}</span>
                  </>
                ) : (
                  '-'
                )}
              </div>
            </div>
            <div className="avalon-card p-2">
              <div className="flex items-center gap-1 text-avalon-text text-xs mb-0.5">
                <Trophy className="w-3 h-3" />
                <span>MVP</span>
              </div>
              <div className="text-lg font-bold text-avalon-gold">
                {(player.total_games || 0) > 0 ? player.mvp_count : '-'}
              </div>
            </div>
          </div>

          {/* V186: 阵营统计 - 胜率逻辑修正：阵营胜率=阵营获胜次数/阵营出场次数 */}
          <div className="avalon-card p-2">
            <h3 className="text-white font-medium mb-1.5 flex items-center gap-2 text-xs">
              <Users className="w-3.5 h-3.5 text-avalon-gold" />
              阵营表现
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {/* V187: 正义阵营胜率 - 左侧，胜率=正义胜场/正义出场次数 */}
              <div className="flex flex-col items-center p-1.5 bg-blue-500/10 rounded-lg border border-blue-500/30">
                <div className="flex items-center gap-1 mb-0.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                  <span className="text-blue-400 font-medium text-xs">正义阵营胜率</span>
                </div>
                <div className="text-center">
                  {(player.total_games || 0) > 0 ? (
                    <>
                      <span className="text-white font-bold text-sm">{player.good_wins}胜</span>
                      <span className="text-avalon-text text-xs ml-1">
                        {(() => {
                          // V186: 计算正义阵营出场次数
                          const goodGames = (player.percival_games || 0) + 
                                           (player.merlin_games || 0) + 
                                           (player.loyal_games || 0) + 
                                           (player.lancelot_good_games || 0);
                          return goodGames > 0 ? Math.round((player.good_wins / goodGames) * 100) + '%' : '0%';
                        })()}
                      </span>
                    </>
                  ) : (
                    <span className="text-avalon-text font-bold text-sm">-</span>
                  )}
                </div>
              </div>
              {/* V187: 邪恶阵营胜率 - 右侧，胜率=邪恶胜场/邪恶出场次数 */}
              <div className="flex flex-col items-center p-1.5 bg-red-500/10 rounded-lg border border-red-500/30">
                <div className="flex items-center gap-1 mb-0.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
                  <span className="text-red-400 font-medium text-xs">邪恶阵营胜率</span>
                </div>
                <div className="text-center">
                  {(player.total_games || 0) > 0 ? (
                    <>
                      <span className="text-white font-bold text-sm">{player.evil_wins}胜</span>
                      <span className="text-avalon-text text-xs ml-1">
                        {(() => {
                          // V186: 计算邪恶阵营出场次数
                          const evilGames = (player.mordred_games || 0) + 
                                           (player.morgana_games || 0) + 
                                           (player.assassin_games || 0) + 
                                           (player.minion_games || 0) + 
                                           (player.oberon_games || 0) +
                                           (player.lancelot_evil_games || 0);
                          return evilGames > 0 ? Math.round((player.evil_wins / evilGames) * 100) + '%' : '0%';
                        })()}
                      </span>
                    </>
                  ) : (
                    <span className="text-avalon-text font-bold text-sm">-</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* V185: 积分统计 - 进一步压缩高度 */}
          <div className="avalon-card p-2">
            <h3 className="text-white font-medium mb-1.5 flex items-center gap-2 text-xs">
              <TrendingUp className="w-3.5 h-3.5 text-avalon-gold" />
              积分统计
            </h3>
            <div className="grid grid-cols-2 gap-2">
              <div className="text-center py-0.5">
                <div className="text-avalon-text text-xs mb-0">总积分</div>
                <div className={`text-base font-bold ${(player.total_games || 0) === 0 ? 'text-avalon-text' : player.total_score >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {(player.total_games || 0) > 0 ? `${player.total_score > 0 ? '+' : ''}${player.total_score}` : '-'}
                </div>
              </div>
              <div className="text-center py-0.5">
                <div className="text-avalon-text text-xs mb-0">场均积分</div>
                <div className={`text-base font-bold ${(player.total_games || 0) === 0 ? 'text-avalon-text' : (player.avg_score || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {(player.total_games || 0) > 0 ? `${(player.avg_score || 0) > 0 ? '+' : ''}${player.avg_score?.toFixed(1) || '0.0'}` : '-'}
                </div>
              </div>
            </div>
          </div>

          {/* V156/V268: 连胜追踪组件（含积分变动） - 在总览页显示 */}
          <WinStreakTracker 
            games={filteredGames} 
            playerNumber={parseInt(playerNumber!)} 
            currentTargetPlayerName={player.player_name}
          />
        </div>
      )}

      {/* V154: 角色统计面板 - 传递过滤后的游戏数据和玩家统计 */}
      {activeTab === 'roles' && (
        <RoleStatsChart 
          playerStats={player} 
          games={filteredGames}
        />
      )}



      {/* V138: 对阵分析使用昵称作为关联要素 */}
      {/* V146: 新增筛选器数据传递 */}
      {activeTab === 'opponents' && (
        <OpponentAnalysis
          games={filteredGames}
          currentPlayerNumber={parseInt(playerNumber!)}
          currentTargetPlayerName={player.player_name}
          seasons={seasons}
          dojos={dojos}
        />
      )}
    </div>
  </ThemedBackground>
  );
}
